import { Badge, Card, EmptyState, cx } from './ui';
import { formatDuration, formatNumber, formatPercent } from '@/lib/format';

/** Outcome tone → the colour it reads as. Matches OUTCOME_TONES on the server. */
const TONE_BAR: Record<string, string> = {
  good: 'bg-emerald-500',
  info: 'bg-purple-500',
  neutral: 'bg-ink-400',
  warn: 'bg-amber-500',
  bad: 'bg-red-500',
};

const TONE_BADGE: Record<string, 'green' | 'purple' | 'gray' | 'amber' | 'red'> = {
  good: 'green',
  info: 'purple',
  neutral: 'gray',
  warn: 'amber',
  bad: 'red',
};

export interface DispositionEntry {
  code: string;
  outcome: string;
  label: string;
  tone: string;
  calls: number;
  pct: number;
}

export interface CampaignDisposition {
  campaign: string;
  /** Which Vicidial server. Two dialers can both run a campaign called HOME01. */
  integration_id: string | null;
  integration_name: string | null;
  calls: number;
  qualified: number;
  transferred: number;
  avg_seconds: number;
  dispositions: DispositionEntry[];
}

export interface CampaignDispositionsData {
  campaigns: CampaignDisposition[];
  totals: DispositionEntry[];
  calls: number;
  window_days?: number;
  /** True when the client runs more than one dialer; drives the server label. */
  multi_dialer?: boolean;
}

/**
 * Vicidial disposition outcomes, per campaign.
 *
 * This is the view that reconciles against Vicidial's own reporting: it shows
 * the code that was actually written back to the dialer, not the platform's
 * internal call state. Percentages are of each campaign's own volume, so a
 * small campaign's DNC rate is comparable to a large one's.
 */
export function CampaignDispositions({
  data,
  title = 'Vicidial dispositions by campaign',
  description,
}: {
  data: CampaignDispositionsData | null | undefined;
  title?: string;
  description?: string;
}) {
  const campaigns = data?.campaigns ?? [];
  // Only label the server once there is more than one — a column that always
  // reads the same is noise for every single-dialer client.
  const showServer =
    data?.multi_dialer ??
    new Set(campaigns.map((campaign) => campaign.integration_id)).size > 1;

  if (campaigns.length === 0) {
    return (
      <Card title={title} description={description}>
        <EmptyState
          title="No dispositions recorded yet"
          description="Outcomes appear here once calls have been dispositioned back to the dialer."
        />
      </Card>
    );
  }

  const unmapped = campaigns
    .flatMap((c) => c.dispositions)
    .filter((d) => d.outcome === 'unmapped');

  return (
    <Card
      title={title}
      description={
        description ??
        `${formatNumber(data?.calls ?? 0)} calls${
          data?.window_days ? ` over ${data.window_days} days` : ''
        }`
      }
      padded={false}
    >
      {unmapped.length > 0 && (
        <div className="border-b border-line bg-amber-50 px-4 py-2.5 text-xs text-amber-800 dark:bg-amber-950/40 dark:text-amber-300">
          {/* An unmapped code means the campaign is using a status the tenant's
              disposition map does not know about — an integration gap, not a
              display problem. */}
          Unmapped disposition {unmapped.length === 1 ? 'code' : 'codes'}:{' '}
          <span className="font-mono">
            {[...new Set(unmapped.map((d) => d.code))].join(', ')}
          </span>
          . Add {unmapped.length === 1 ? 'it' : 'them'} to this client&apos;s
          disposition map so outcomes are classified correctly.
        </div>
      )}

      <ul className="divide-y divide-line">
        {campaigns.map((campaign) => (
          // Keyed on server + campaign: the campaign id alone is not unique
          // across dialers, and duplicate keys make React reuse the wrong row.
          <li
            key={`${campaign.integration_id ?? 'none'}:${campaign.campaign}`}
            className="px-4 py-3.5"
          >
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <div className="flex items-baseline gap-2">
                <span className="font-mono text-sm font-medium text-ink-900 dark:text-ink-50">
                  {campaign.campaign}
                </span>
                {showServer && (
                  <Badge tone="blue">{campaign.integration_name ?? 'No server'}</Badge>
                )}
                <span className="text-xs text-ink-500 dark:text-ink-400">
                  {formatNumber(campaign.calls)} calls · avg{' '}
                  {formatDuration(campaign.avg_seconds)}
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                <Badge tone="green">
                  {formatNumber(campaign.transferred)} transferred
                </Badge>
                <span className="text-xs text-ink-500 dark:text-ink-400">
                  {campaign.calls > 0
                    ? formatPercent((campaign.transferred / campaign.calls) * 100)
                    : '—'}
                </span>
              </div>
            </div>

            {/* Stacked bar: proportions at a glance, exact numbers below. */}
            <div
              className="mt-2 flex h-2.5 overflow-hidden rounded-full bg-ink-100 dark:bg-ink-800"
              role="img"
              aria-label={
                showServer
                  ? `Disposition mix for ${campaign.campaign} on ${
                      campaign.integration_name ?? 'no server'
                    }`
                  : `Disposition mix for ${campaign.campaign}`
              }
            >
              {campaign.dispositions.map((entry) => (
                <div
                  key={entry.code}
                  className={cx(TONE_BAR[entry.tone] ?? TONE_BAR.neutral)}
                  style={{ width: `${entry.pct}%` }}
                  title={`${entry.code} — ${entry.label}: ${entry.calls} (${entry.pct}%)`}
                />
              ))}
            </div>

            <div className="mt-2.5 overflow-x-auto">
              <table className="glee-table glee-table--compact text-xs">
                <tbody>
                  {campaign.dispositions.map((entry) => (
                    <tr key={entry.code} className="align-middle">
                      <td className="w-6">
                        <span
                          className={cx(
                            'inline-block h-2 w-2 rounded-full',
                            TONE_BAR[entry.tone] ?? TONE_BAR.neutral,
                          )}
                        />
                      </td>
                      <td>
                        <span className="font-mono text-ink-900 dark:text-ink-50">
                          {entry.code}
                        </span>
                      </td>
                      <td className="text-ink-600 dark:text-ink-300">
                        {entry.label}
                        {entry.outcome === 'unmapped' && (
                          <Badge tone="amber" className="ml-1.5">
                            unmapped
                          </Badge>
                        )}
                      </td>
                      <td className="text-right tabular-nums text-ink-800 dark:text-ink-100">
                        {formatNumber(entry.calls)}
                      </td>
                      <td className="w-14 text-right tabular-nums text-ink-500">
                        {formatPercent(entry.pct)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </li>
        ))}
      </ul>

      {(data?.totals?.length ?? 0) > 0 && campaigns.length > 1 && (
        <div className="border-t border-line px-4 py-3">
          <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-ink-400">
            All campaigns
          </p>
          <div className="flex flex-wrap gap-1.5">
            {data!.totals.map((entry) => (
              <Badge key={entry.code} tone={TONE_BADGE[entry.tone] ?? 'gray'}>
                {entry.code} · {formatNumber(entry.calls)} ({formatPercent(entry.pct)})
              </Badge>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}
